import { IconOtherSound } from '@doltech/icons/IconOtherSound';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { getNodeText } from '../../../../../figma/BlogEditor/plugins/shared-transforms';
import { colorsV2 } from '../../../../../figma/colors-v2';
import { Typography } from '../../../../../figma/DesignSystemV2/Typography';
import { shadows } from '../../../../../figma/shadows';
import { VocabRenderElementProps } from '../types';
import { VocabFlowElementMain } from './shared-styles';
import { DolPopover } from '../../../../../figma/Popover/DolPopover';
import { useSimpleAudioPlayInBackgroundStore } from '../../../../../hooks/simple-audio-play-in-background.store';

const PopOverVocabMain = styled.div`
  padding: 12px 16px;
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr 32px;
  width: 280px;

  border: 1px solid ${colorsV2.black10};
  background: ${colorsV2.white100};
  backdrop-filter: blur(5px);
  border-radius: 8px;
  box-shadow: ${shadows.zIndex5};

  .content {
    display: grid;
    gap: 4px;
    .word {
      text-transform: capitalize;
    }
  }

  .play-button {
    width: 32px;
    height: 32px;
    background: ${colorsV2.white100};
    box-shadow: 0 16px 32px rgba(45, 55, 66, 0.1);
    border-radius: 20px;
    display: grid;
    place-items: center;
  }
`;

const InlineVocabMain = styled.span`
  cursor: pointer;
  && {
    .action {
      display: inline;
    }

    .play-button {
      margin-left: 4px;
      width: 32px;
      height: 32px;
      background: ${colorsV2.white100};
      box-shadow: 0 16px 32px rgba(45, 55, 66, 0.1);
      border-radius: 20px;
      display: inline-grid;
      place-items: center;
    }
  }
`;

export const VocabFlowReadOnlyElement = observer((props: VocabRenderElementProps) => {
  const { element } = props;
  const audioPlayInBackgroundStore = useSimpleAudioPlayInBackgroundStore();
  const text = getNodeText(element);

  const playAudio = React.useCallback(() => {
    if (element.spelling) {
      audioPlayInBackgroundStore.playByPhonetics(element.spelling);
      return;
    }
    audioPlayInBackgroundStore.playByText(text);
  }, [audioPlayInBackgroundStore, element.spelling, text]);

  if (element.displayType === 'inline') {
    if (element.spelling) {
      return (
        <InlineVocabMain onClick={playAudio}>
          <VocabFlowElementMain className="vocab-highlight" {...props} />
          <div aria-hidden className="action">
            <div className="play-button">
              <IconOtherSound fontSize={16} />
            </div>
          </div>
        </InlineVocabMain>
      );
    }
    return <VocabFlowElementMain className="vocab-highlight" {...props} />;
  }

  if (element.displayType !== 'inline' && element.meaning) {
    return (
      <DolPopover
        overlayClassName="un-styled no-arrow custom-inner-content"
        trigger="hover"
        content={
          <PopOverVocabMain>
            <div className="content">
              <Typography.Paragraph className="word" variant="semi-bold/16">
                {text}
              </Typography.Paragraph>
              <Typography.Paragraph className="meaning" variant="regular/16">
                {element.meaning}
              </Typography.Paragraph>
            </div>
            <div aria-hidden className="action" onClick={playAudio}>
              <div className="play-button">
                <IconOtherSound fontSize={16} />
              </div>
            </div>
          </PopOverVocabMain>
        }
      >
        <VocabFlowElementMain onClick={playAudio} className="vocab-highlight" {...props} />
      </DolPopover>
    );
  }
  return <VocabFlowElementMain className="vocab-highlight" {...props} />;
});
